import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaPhoneTreeV1Service } from '../service';
import { ListByTenantsIO } from '../types';

export const getListByTenantsQueryKey = (req: ListByTenantsIO['input']) =>
  ['PhoneTreeV1', 'ListByTenants', req] as const;
type QueryKey = ReturnType<typeof getListByTenantsQueryKey>;

/**
 * A hook that returns a query for the `ListByTenants` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useListByTenantsQuery = <E = unknown, D = ListByTenantsIO['output']>(
  req: ListByTenantsIO['input'],
  options?: LimitedSchemaQueryOptions<ListByTenantsIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<ListByTenantsIO['output'], E, D, QueryKey>({
    queryKey: getListByTenantsQueryKey(req),
    queryFn: () => SchemaPhoneTreeV1Service.ListByTenants(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `ListByTenants` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useListByTenantsQueries = <E = unknown, D = ListByTenantsIO['output']>(
  argsArr: {
    req: ListByTenantsIO['input'];
    options?: LimitedSchemaQueryOptions<ListByTenantsIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getListByTenantsQueryKey(req),
      queryFn: () => SchemaPhoneTreeV1Service.ListByTenants(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useListByTenantsQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `ListByTenants` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchListByTenants = () => {
  const queryClient = useQueryClient();

  const fetchListByTenants = async <E = unknown, D = ListByTenantsIO['output']>(
    req: ListByTenantsIO['input'],
    options?: LimitedSchemaFetchQueryOptions<ListByTenantsIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<ListByTenantsIO['output'], E, D, QueryKey>(
      getListByTenantsQueryKey(req),
      () => SchemaPhoneTreeV1Service.ListByTenants(req, httpOptions),
      options
    );

    return response;
  };

  return fetchListByTenants;
};
